import { Users, Layers, Globe, ArrowRight } from 'lucide-react';
import type { TabRoleSegmentAdaptation } from '../../../../types/craft';

interface Props {
  data: TabRoleSegmentAdaptation;
}

export default function RoleSegmentAdaptationTab({ data }: Props) {
  const hasData = data?.role_adaptations?.length || data?.segment_adaptations?.length;
  if (!hasData) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.role_adaptations?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-app-focus" />
            <h4 className="text-sm font-semibold text-app-ink">Role Adaptations</h4>
          </div>
          <div className="space-y-3">
            {data.role_adaptations.map((r, i) => (
              <div key={i} className="bg-app-surface border border-app-line rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-app-focus/10 text-app-focus">
                    {r.role}
                  </span>
                </div>
                <p className="text-xs text-app-ink leading-relaxed mb-2">{r.how_it_applies}</p>
                {r.key_differences && (
                  <p className="text-xs text-app-muted mb-2"><span className="font-medium">Key differences:</span> {r.key_differences}</p>
                )}
                {r.focus_areas?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.focus_areas.map((f, j) => (
                      <span key={j} className="px-2 py-1 bg-app-canvas text-app-muted rounded text-[10px] font-medium">
                        {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.segment_adaptations?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-app-positive" />
            <h4 className="text-sm font-semibold text-app-ink">Segment Adaptations</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.segment_adaptations.map((s, i) => (
              <div key={i} className="bg-app-surface border border-app-line rounded-lg p-4">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-app-positive/10 text-app-positive inline-block mb-2">
                  {s.segment}
                </span>
                <p className="text-xs text-app-ink leading-relaxed mb-2">{s.how_it_differs}</p>
                {s.key_adjustments?.length > 0 && (
                  <ul className="space-y-1">
                    {s.key_adjustments.map((a, j) => (
                      <li key={j} className="flex items-start gap-1.5 text-xs text-app-muted">
                        <ArrowRight className="w-3 h-3 text-app-positive mt-0.5 flex-shrink-0" />
                        {a}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.motion_notes?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Sales Motion Notes</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.motion_notes.map((n, i) => (
              <div key={i} className="bg-app-surface border border-app-line rounded-lg p-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-app-muted">{n.motion}</span>
                <p className="text-xs text-app-ink mt-0.5">{n.note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.sector_notes?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-app-warning" />
            <h4 className="text-sm font-semibold text-app-ink">Sector-Specific Notes</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.sector_notes.map((s, i) => (
              <div key={i} className="bg-app-warning/10 border border-app-warning rounded-lg p-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-app-warning">{s.sector}</span>
                <p className="text-xs text-app-warning mt-0.5">{s.adaptation}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
